import type { ComponentPropsWithoutRef, ReactNode, ElementType } from 'react';
import { forwardRef } from 'react';
import { styled, css } from 'styled-components';
import { isNotNil } from '@wistia/type-guards';
import { ellipsisStyle } from '../Ellipsis/Ellipsis';
import type { ColorSchemeTypes } from '../ColorSchemeWrapper';
import { lineClampCss } from '../../css/lineClampCss';
import { getColorScheme } from '../../private/helpers/getColorScheme';
import { makePolymorphic } from '../../private/helpers/makePolymorphic';
import { applyMaxCharsToChildren } from '../../private/helpers/applyMaxCharsToChildren';
import { iconInlineStyle } from '../Icon/Icon';

type AlignmentTypes = 'center' | 'justify' | 'left' | 'right';

const sharedBodyStyle = css`
  > strong,
  b {
    font-weight: var(--wui-typography-weight-body-bold);
  }

  > mark {
    font-weight: var(--wui-typography-weight-body-mark);
  }
`;

const labelBoldStyles = css`
  > strong,
  b {
    font-weight: var(--wui-typography-weight-label-bold);
  }

  > mark {
    font-weight: var(--wui-typography-weight-label-mark);
  }
`;

const body1TextStyle = css`
  --font-family: var(--wui-typography-body-1-family);
  --font-size: var(--wui-typography-body-1-size);
  --font-weight: var(--wui-typography-body-1-weight);
  --line-height: var(--wui-typography-body-1-line-height);
  --paragraph-spacing: var(--wui-typography-body-1-paragraph-spacing);
  ${sharedBodyStyle}
`;

const body2TextStyle = css`
  --font-family: var(--wui-typography-body-2-family);
  --font-size: var(--wui-typography-body-2-size);
  --font-weight: var(--wui-typography-body-2-weight);
  --line-height: var(--wui-typography-body-2-line-height);
  --paragraph-spacing: var(--wui-typography-body-2-paragraph-spacing);
  ${sharedBodyStyle}
`;

const body3TextStyle = css`
  --font-family: var(--wui-typography-body-3-family);
  --font-size: var(--wui-typography-body-3-size);
  --font-weight: var(--wui-typography-body-3-weight);
  --line-height: var(--wui-typography-body-3-line-height);
  --paragraph-spacing: var(--wui-typography-body-3-paragraph-spacing);
  ${sharedBodyStyle}
`;

const body4TextStyle = css`
  --font-family: var(--wui-typography-body-4-family);
  --font-size: var(--wui-typography-body-4-size);
  --font-weight: var(--wui-typography-body-4-weight);
  --line-height: var(--wui-typography-body-4-line-height);
  --paragraph-spacing: var(--wui-typography-body-4-paragraph-spacing);
  ${sharedBodyStyle}
`;

const label1TextStyle = css`
  --font-family: var(--wui-typography-label-1-family);
  --font-size: var(--wui-typography-label-1-size);
  --font-weight: var(--wui-typography-label-1-weight);
  --line-height: var(--wui-typography-label-1-line-height);
  ${labelBoldStyles}
`;

const label2TextStyle = css`
  --font-family: var(--wui-typography-label-2-family);
  --font-size: var(--wui-typography-label-2-size);
  --font-weight: var(--wui-typography-label-2-weight);
  --line-height: var(--wui-typography-label-2-line-height);
  ${labelBoldStyles}
`;

const label3TextStyle = css`
  --font-family: var(--wui-typography-label-3-family);
  --font-size: var(--wui-typography-label-3-size);
  --font-weight: var(--wui-typography-label-3-weight);
  --line-height: var(--wui-typography-label-3-line-height);
  ${labelBoldStyles}
`;

const label4TextStyle = css`
  --font-family: var(--wui-typography-label-4-family);
  --font-size: var(--wui-typography-label-4-size);
  --font-weight: var(--wui-typography-label-4-weight);
  --line-height: var(--wui-typography-label-4-line-height);
  ${labelBoldStyles}
`;

const body1MonoTextStyle = css`
  ${body1TextStyle};
  --font-family: var(--wui-typography-family-mono);
`;

const body2MonoTextStyle = css`
  ${body2TextStyle};
  --font-family: var(--wui-typography-family-mono);
`;

const body3MonoTextStyle = css`
  ${body3TextStyle};
  --font-family: var(--wui-typography-family-mono);
`;

const body4MonoTextStyle = css`
  ${body4TextStyle};
  --font-family: var(--wui-typography-family-mono);
`;

export const variantStyleMap = {
  body1: body1TextStyle,
  body2: body2TextStyle,
  body3: body3TextStyle,
  body4: body4TextStyle,
  body1Mono: body1MonoTextStyle,
  body2Mono: body2MonoTextStyle,
  body3Mono: body3MonoTextStyle,
  body4Mono: body4MonoTextStyle,
  label1: label1TextStyle,
  label2: label2TextStyle,
  label3: label3TextStyle,
  label4: label4TextStyle,
};

const bodyElement = 'p';
const labelElement = 'span';
const DEFAULT_ELEMENT = bodyElement;

export const variantElementMap: Record<string, ElementType> = {
  body1: DEFAULT_ELEMENT,
  body2: DEFAULT_ELEMENT,
  body3: DEFAULT_ELEMENT,
  body4: DEFAULT_ELEMENT,
  body1Mono: DEFAULT_ELEMENT,
  body2Mono: DEFAULT_ELEMENT,
  body3Mono: DEFAULT_ELEMENT,
  body4Mono: DEFAULT_ELEMENT,
  label1: labelElement,
  label2: labelElement,
  label3: labelElement,
  label4: labelElement,
};

export type TextProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * The horizontal alignment
   * <br />
   * _Note: this only affects block elements_
   */
  align?: AlignmentTypes;
  /**
   * The text content
   */
  children?: ReactNode | undefined;
  /**
   * Sets the [color scheme](../?path=/docs/docs-color-schemes--docs)
   */
  colorScheme?: ColorSchemeTypes;
  /**
   * Allows user to override default button colors
   */
  disabled?: boolean;
  /**
   * Display the text as inline content
   */
  inline?: boolean;
  /**
   * Maximum number of characters to display before truncating. Must be >= 1.
   * When truncated, an ellipsis (...) will be added to the end.
   */
  maxChars?: number;
  /**
   * Prevents text from being highlighted and copied
   */
  preventUserSelect?: boolean;
  /**
   * Text prominence affects color. Use 'primary' for main non-interactive text, 'secondary' for supporting text, and 'button' in specific cases where text is used in an interactive component.
   */
  prominence?: 'button' | 'primary' | 'secondary';
  /**
   * This will inherit the jsdoc block from PolymorphicComponent
   */
  renderAs?: ElementType;
  /**
   * Number of lines to display before truncating with an ellipsis.
   * Set to 1 to truncate after a single line, 2 for two lines, etc.
   * <br />
   * _Note: this only affects block elements_
   */
  maxLines?: number;
  /**
   * The text style to display
   */
  variant?: keyof typeof variantStyleMap | undefined;
};

type StyledTextProps = {
  $align: AlignmentTypes;
  $colorScheme: ColorSchemeTypes;
  $disabled: boolean;
  $inline: boolean;
  $preventUserSelect: boolean;
  $prominence: 'button' | 'primary' | 'secondary';
  $maxLines: number | undefined;
  $variant: keyof typeof variantStyleMap;
  as: ElementType;
};

const StyledText = styled.div<StyledTextProps>`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  --text-color: ${({ $prominence, $disabled }) => {
    if ($disabled) {
      return 'var(--wui-color-text-disabled)';
    }
    if ($prominence === 'secondary') {
      return 'var(--wui-color-text-secondary)';
    }
    if ($prominence === 'button') {
      return 'var(--wui-color-text-button)';
    }
    return 'var(--wui-color-text)';
  }};

  ${iconInlineStyle}
  color: var(--text-color);
  font-family: var(--font-family);
  font-size: var(--font-size);
  font-weight: var(--font-weight);
  line-height: var(--line-height);
  font-style: normal;
  margin: 0;
  ${({ $variant }) => variantStyleMap[$variant]}
  ${({ $maxLines }) => {
    if (isNotNil($maxLines)) {
      return css`
        ${ellipsisStyle};
        ${lineClampCss($maxLines)};
      `;
    }
    return undefined;
  }}
  ${({ $inline }) =>
    $inline &&
    css`
      display: inline-block;
    `}
  ${({ $disabled }) =>
    $disabled &&
    css`
      --text-color: var(--wui-color-text-disabled);
    `}
  ${({ $preventUserSelect }) =>
    $preventUserSelect &&
    css`
      user-select: none;
    `}
  ${({ $align }) => css`
    text-align: ${$align};
  `}
  ${({ as }) =>
    as === 'p' &&
    css`
      display: block;

      + p {
        margin-top: var(--paragraph-spacing, var(--wui-space-04));
      }
    `}
`;

const TextComponent = forwardRef<typeof DEFAULT_ELEMENT, TextProps>(
  (
    {
      align = 'left',
      children,
      colorScheme = 'inherit',
      disabled = false,
      inline = false,
      maxChars,
      preventUserSelect = false,
      prominence = 'primary',
      maxLines,
      variant = 'body2',
      renderAs,
      ...props
    },
    ref,
  ) => {
    const processedChildren = applyMaxCharsToChildren(children, maxChars);

    return (
      <StyledText
        ref={ref}
        $align={align}
        $colorScheme={colorScheme}
        $disabled={disabled}
        $inline={inline}
        $maxLines={maxLines}
        $preventUserSelect={preventUserSelect}
        $prominence={prominence}
        $variant={variant}
        as={renderAs ?? variantElementMap[variant] ?? DEFAULT_ELEMENT}
        {...props}
      >
        {processedChildren}
      </StyledText>
    );
  },
);

TextComponent.displayName = 'Text';

/**
 * Used for rending paragraphs and inline text.
 */
// @ts-expect-error makePolymorphic is causing issues with types in R19
export const Text = makePolymorphic<typeof DEFAULT_ELEMENT, TextProps>(TextComponent);
